import { EChannels, EImportFileType } from 'core/model/enums';
import { ECreditActions } from 'core/model/enums/distributor.enum';
import type {
  IBranch,
  IBranchesList,
  IGetBranchClientExclusion,
  IToggleBranchCreditParams,
  IToggleBranchPurchaseLimitParams,
  IToggleBranchUserDataUpdateParams,
  IUpsertBranchClientExclusionParams,
  IUpsertBranchParams,
} from 'core/model/interfaces/distributors/branches.interface';
import { toPriceGroupList } from '../master-entities/master-entities.mappers';
import type {
  IBranchDTO,
  IBranchPurchaseLimitDTO,
  IBranchScheduleDTO,
  IGetBranchClientExclusionDTO,
  IGetBranchesListResponse,
  IToggleBranchCreditDTO,
  IToggleBranchCreditParamsDTO,
  IToggleBranchPurchaseLimitParamsDTO,
  IToggleBranchUserDataUpdateParamsDTO,
  IUpdateBranchParamsDTO,
} from './branches.dto';

export const mapBranchItem = (item: IBranchDTO): IBranch => {
  const branch: IBranch = {
    branchId: item.id,
    branchName: item.name,
    distributor: {
      distributorId: item.distributor.id,
      distributorName: item.distributor.name,
    },
    schedules: item.schedules.map(schedule => ({
      weekday: schedule.weekday,
      time: schedule.time,
    })),
    purchaseLimits: item.purchaseLimits || [],
    credit: {
      enabled: [],
      priceGroups: toPriceGroupList(item.credit.priceGroups),
    },
    userDataUpdate: {
      enabled: item.updateMandatory?.enabled || false,
    },
  };

  if (item.credit.isDiaDiaEnabled !== undefined)
    branch.credit.enabled.push({
      channelId: EChannels.DIA_DIA,
      status: item.credit.isDiaDiaEnabled,
    });
  if (item.credit.isInsumaEnabled !== undefined)
    branch.credit.enabled.push({
      channelId: EChannels.INSUMA,
      status: item.credit.isInsumaEnabled,
    });

  return branch;
};

/**
 * @todo Remove the `purchaseLimit` validation when backend confirms that the field is always returned with data.
 */
export const mapDtoToBranches = (response: IGetBranchesListResponse): IBranchesList => {
  const list: IBranchesList = {
    branches: response.branches.map(mapBranchItem),
    pagination: response.pagination,
  };

  return list;
};

export const toBranchDto = (data: IUpsertBranchParams): IUpdateBranchParamsDTO => {
  const schedules = data.schedules.map(
    (schedule): IBranchScheduleDTO => ({
      weekday: schedule.weekday,
      time: schedule.time,
    }),
  );

  const purchaseLimits = data.purchaseLimits.map(
    (limit): IBranchPurchaseLimitDTO => ({
      channelId: limit.channelId,
      limit: limit.limit ? Number(limit.limit) : null,
      enabled: limit.enabled,
    }),
  );

  const credit: IToggleBranchCreditDTO = {
    isInsumaEnabled: data.credit.enabled.find(item => item.channelId === EChannels.INSUMA)?.status as boolean,
    isDiaDiaEnabled: data.credit.enabled.find(item => item.channelId === EChannels.DIA_DIA)?.status as boolean,
    priceListIds: data.credit.priceListIds.map(Number),
  };

  const branch: IUpdateBranchParamsDTO = {
    branchId: data.branchId,
    distributorId: data.distributorId,
    schedules,
    purchaseLimits,
    credit,
  };

  return branch;
};

export const toTogglePurchaseLimitDto = (
  data: IToggleBranchPurchaseLimitParams,
): IToggleBranchPurchaseLimitParamsDTO => {
  const { branch, purchaseLimit } = data;

  const purchaseLimits = branch.purchaseLimits.map(
    (limit): IBranchPurchaseLimitDTO => ({
      channelId: limit.channelId,
      limit: limit.limit ? Number(limit.limit) : null,
      enabled: purchaseLimit.channelId === limit.channelId ? !limit.enabled : limit.enabled,
    }),
  );

  const updatedBranch: IToggleBranchPurchaseLimitParamsDTO = {
    branchId: branch.branchId,
    distributorId: branch.distributor.distributorId,
    purchaseLimits,
  };

  return updatedBranch;
};

export const toToggleCreditDto = (data: IToggleBranchCreditParams): IToggleBranchCreditParamsDTO => {
  const { branch, credit } = data;

  const priceListIds = credit.priceGroups
    .map(priceGroup => priceGroup.priceLists)
    .flat()
    .map(priceList => priceList.priceListId);

  const updatedCredit: IToggleBranchCreditParamsDTO = {
    branchId: branch.branchId,
    distributorId: branch.distributor.distributorId,
    credit: {
      isInsumaEnabled: credit.enabled.find(item => item.channelId === EChannels.INSUMA)?.status as boolean,
      isDiaDiaEnabled: credit.enabled.find(item => item.channelId === EChannels.DIA_DIA)?.status as boolean,
      priceListIds,
    },
  };

  return updatedCredit;
};

export const toToggleUserDataUpdateDto = (
  data: IToggleBranchUserDataUpdateParams,
): IToggleBranchUserDataUpdateParamsDTO => {
  const { branch, userDataUpdate } = data;

  const updatedUserData: IToggleBranchUserDataUpdateParamsDTO = {
    branchId: branch.branchId,
    distributorId: branch.distributor.distributorId,
    isUserMandatory: userDataUpdate.enabled,
  };

  return updatedUserData;
};

export const toUpsertBranchCustomersExclusion = (params: IUpsertBranchClientExclusionParams) => {
  const { file, creditAction } = params;

  const formData = new FormData();

  formData.append('file', file.blob);
  formData.append('metadata', JSON.stringify({ creditOperatingMode: Number(creditAction) }));
  formData.append('fileType', EImportFileType.EXCLUDE_CREDIT);

  return formData;
};

export const mapDtoToGetBranchClientExclusion = (dto: IGetBranchClientExclusionDTO): IGetBranchClientExclusion => {
  const response: IGetBranchClientExclusion = {
    date: dto.date,
    filePath: dto.filePath,
    templateUrl: dto.templateUrl,
    userName: dto.userName,
    filename: dto.downloadFilename,
    creditAction: dto.metadata?.creditOperatingMode
      ? (`${dto.metadata.creditOperatingMode}` as ECreditActions)
      : ECreditActions.EXCLUDE_CUSTOMERS,
  };

  return response;
};
